// src/js/storage.web.js
import { Preferences } from '@capacitor/preferences';

const WEB_STORAGE_KEY = 'cashbook_web_transactions';

// Helper function to get all transactions from Preferences
async function _getAllTransactions() {
    const { value } = await Preferences.get({ key: WEB_STORAGE_KEY });
    return value ? JSON.parse(value) : [];
}

// Helper function to set all transactions to Preferences
async function _setAllTransactions(transactions) {
    await Preferences.set({ key: WEB_STORAGE_KEY, value: JSON.stringify(transactions) });
}

class WebStorageService {
    async initializeDatabase() {
        // Not needed for Preferences, but the method must exist to match the native interface.
        console.log('Web storage (Preferences) is ready.');
        return Promise.resolve();
    }

    async getTransactions(userId = null, date = new Date()) {
        const all = await _getAllTransactions();
        
        // 如果 date 為 null，則獲取該用戶的所有紀錄
        if (date === null) {
            return all.filter(tx => tx.user_id === userId && !tx.is_deleted);
        }

        const year = date.getFullYear();
        const month = date.getMonth();
        const yearStr = String(year);
        const monthStr = String(month + 1).padStart(2, '0');
        const targetPrefix = `${yearStr}-${monthStr}`;

        return all.filter(tx => {
            if (tx.user_id !== userId || tx.is_deleted) {
                return false;
            }
            return tx.date && tx.date.startsWith(targetPrefix);
        });
    }

    async addTransaction(tx) {
        const all = await _getAllTransactions();
        const now = new Date().toISOString();
        
        // Simple auto-incrementing ID for web, similar to SQLite.
        const newId = all.length > 0 ? Math.max(...all.map(t => t.id)) + 1 : 1;

        const newTx = {
            ...tx,
            id: newId,
            server_id: null,
            created_at: now,
            last_modified: now,
            sync_status: 'new',
            is_deleted: 0,
        };

        all.push(newTx);
        await _setAllTransactions(all);
        return newId;
    }
    
    async updateTransaction(id, tx) {
        let all = await _getAllTransactions();
        const now = new Date().toISOString();

        all = all.map(existingTx => {
            if (existingTx.id === id) {
                return {
                    ...existingTx,
                    ...tx,
                    last_modified: now,
                    sync_status: 'modified',
                };
            }
            return existingTx;
        });

        await _setAllTransactions(all);
    }

    async deleteTransaction(id) {
        let all = await _getAllTransactions();
        const now = new Date().toISOString();
        
        all = all.map(tx => {
            if (tx.id === id) {
                return {
                    ...tx,
                    is_deleted: 1,
                    last_modified: now,
                    sync_status: 'modified',
                };
            }
            return tx;
        });

        await _setAllTransactions(all);
    }

    // --- Sync Specific Methods ---

    async getUnsyncedTransactions(userId) {
        const all = await _getAllTransactions();
        return all.filter(tx => tx.user_id === userId && tx.sync_status !== 'synced');
    }

    async upsertTransactionsFromServer(transactions) {
        let all = await _getAllTransactions();
        let updateCount = 0;
        let skipCount = 0;
        
        transactions.forEach(serverTx => {
            const index = all.findIndex(t => t.server_id === serverTx.id);
            
            // 如果本地有這筆資料，且狀態是 modified 或 new，我們跳過覆蓋，保護本地最新修改
            if (index !== -1 && (all[index].sync_status === 'modified' || all[index].sync_status === 'new')) {
                console.log(`Skipping server update for record ${all[index].id} to protect local changes.`);
                skipCount++;
                return;
            }

            const txData = {
                server_id: serverTx.id,
                user_id: serverTx.user_id,
                amount: serverTx.amount,
                description: serverTx.description,
                type: serverTx.type,
                date: serverTx.date,
                category: serverTx.category,
                created_at: serverTx.created_at,
                last_modified: serverTx.modified_at || serverTx.created_at,
                sync_status: 'synced',
                is_deleted: serverTx.is_deleted ? 1 : 0
            };

            if (index !== -1) {
                all[index] = { ...all[index], ...txData };
                updateCount++;
            } else {
                const newId = all.length > 0 ? Math.max(...all.map(t => t.id)) + 1 : 1;
                all.push({ id: newId, ...txData });
                updateCount++;
            }
        });

        console.log(`Server Upsert: ${updateCount} applied, ${skipCount} skipped.`);
        await _setAllTransactions(all);
    }

    async markTransactionsAsSynced(updates) {
        let all = await _getAllTransactions();
        
        updates.forEach(update => {
            const index = all.findIndex(t => t.id === update.local_id);
            if (index !== -1) {
                all[index].server_id = update.server_id;
                all[index].sync_status = 'synced';
            }
        });

        await _setAllTransactions(all);
    }
}

export const webStorageService = new WebStorageService();
